/**
 * Implementation for provider caching. Used by `valueProviderCache` and `subFormProviderCache`.
 *
 * @template ProviderType - The type of provider implementing the `Provider` interface.
 * @template ValueType - The type of value the provider returns.
 */
export class ProviderCache {
    /**
     * Create a cache instance.
     *
     * @param {(provider: ProviderType, params?: unknown) => Promise<ValueType>} valueGetter - Defines how to call a provider to get its value.
     * @param {number} [maxCacheAgeMillisDefault=600000] - Maximum time in milliseconds for cached values to remain valid. Defaults to 10 minutes.
     */
    constructor(valueGetter, maxCacheAgeMillisDefault = 10 * 1000 * 60) {
        this.valueGetter = valueGetter;
        this.maxCacheAgeMillisDefault = maxCacheAgeMillisDefault;
        this.cachedPromises = {};
        this.cachedValues = {};
        /**
         * Gets a value from cache. If it's not cached than calls provider to get its value and puts that value in cache.
         * If provider throw an error (reject a promise) then this error is not cached and next call to this function will
         * invoke the provider to get its value again.
         *
         * @param {ProviderType} provider - Provider for the value, (`provider.type` is the first part of cache key).
         * @param {unknown} [params] - Optional params to pass to the provider (second part of cache key).
         * @returns {Promise<ValueType>} - A promise resolving to the value.
         */
        this.getValue = (provider, params) => {
            var _a;
            if (provider.disableCache) {
                return this.valueGetter(provider, params);
            }
            const now = Date.now();
            const value = get(this.cachedValues, provider.type, params, now);
            if (value) {
                return Promise.resolve(value);
            }
            let promise = get(this.cachedPromises, provider.type, params, now);
            if (promise) {
                return promise;
            }
            const expires = Date.now() + ((_a = provider.maxCacheAgeMillis) !== null && _a !== void 0 ? _a : this.maxCacheAgeMillisDefault);
            promise = this.valueGetter(provider, params).then((values) => {
                set(this.cachedValues, provider.type, params, values, expires);
                return values;
            }, (error) => {
                set(this.cachedPromises, provider.type, params, undefined, expires);
                throw error;
            });
            set(this.cachedPromises, provider.type, params, promise, expires);
            return promise;
        };
        /**
         * Gets value from cache. Returns `undefined` if value is not cached.
         *
         * @param {string} type - Type of the provider (first part of cache key).
         * @param {unknown} [params] - Params that were passed to the provider to get its value (second part of cache key).
         * @returns {(ValueType | undefined)} - The cached value or undefined if not found or expired.
         */
        this.getCachedValue = (type, params) => {
            return get(this.cachedValues, type, params, Date.now());
        };
        /**
         * Explicitly sets cached value. Can be used to preload cache when application initializes or inside unit tests.
         * Note that any pending promises from `getValue` will not return this value, they will resolve normally and when
         * they do they will set their own cache value.
         *
         * @param {string} type - Type of the provider (first part of cache key).
         * @param {unknown} [params] - Params that were passed to the provider to get its value (second part of cache key).
         * @param {ValueType} value - Value to put in cache.
         */
        this.setCachedValue = (type, params, value) => {
            set(this.cachedValues, type, params, value, Date.now() + this.maxCacheAgeMillisDefault);
        };
        /**
         * Clears cache by provider type.
         *
         * @param {string} type - Type of the provider (first part of cache key).
         */
        this.clearCacheByType = (type) => {
            delete this.cachedPromises[type];
            delete this.cachedValues[type];
        };
        /**
         * Clears all cache.
         * Note that any pending promises will resolve normally and when they do they will set their own cache value.
         */
        this.clearAllCache = () => {
            Object.keys(this.cachedPromises).forEach((key) => delete this.cachedPromises[key]);
            Object.keys(this.cachedValues).forEach((key) => delete this.cachedValues[key]);
        };
    }
}
function get(cache, type, params, now) {
    const cacheByType = cache[type];
    if (cacheByType) {
        const cacheByParams = cacheByType.find((value) => Object.is(params, value.params));
        if (cacheByParams && cacheByParams.expires >= now) {
            return cacheByParams.value;
        }
    }
    return undefined;
}
function set(cache, type, params, value, expires) {
    let cacheByType = cache[type];
    if (!cacheByType) {
        cacheByType = [];
        cache[type] = cacheByType;
    }
    const cacheByParamsIndex = cacheByType.findIndex((value) => Object.is(params, value.params));
    if (value != null) {
        if (cacheByParamsIndex === -1) {
            cacheByType.push({ params, value, expires });
        }
        else {
            const cacheElement = cacheByType[cacheByParamsIndex];
            cacheElement.value = value;
            cacheElement.expires = expires;
        }
    }
    else if (cacheByParamsIndex !== -1) {
        cacheByType.splice(cacheByParamsIndex, 1);
    }
}
